import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link.
   * Respects modifier keys for opening in new tab/window.
   *
   * @param {Event} event
   * @param {URL} url
   */
  #navigateToURL = (event, url) => {
    const shouldOpenInNewTab =
      event instanceof MouseEvent &&
      (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;

    if (!(link instanceof HTMLAnchorElement)) {
      throw new Error('Product card link not found');
    }

    this.#handleQuickAdd();

    this.addEventListener(
      ThemeEvents.variantUpdate,
      this.#handleVariantUpdate
    );

    this.addEventListener(
      ThemeEvents.variantSelected,
      this.#handleVariantSelected
    );

    this.addEventListener(
      SlideshowSelectEvent.eventName,
      this.#handleSlideshowSelect
    );

    mediaQueryLarge.addEventListener(
      'change',
      this.#handleQuickAdd
    );

    this.addEventListener(
      'click',
      this.navigateToProduct
    );

    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener(
      'click',
      this.navigateToProduct
    );
  }

  #preloadNextPreviewImage() {
    const currentSlide =
      this.refs.slideshow?.slides?.[
        this.refs.slideshow?.current
      ];

    currentSlide?.nextElementSibling
      ?.querySelector('img[loading="lazy"]')
      ?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener(
      'pointerenter',
      this.#fetchProductPageHandler
    );

    this.removeEventListener(
      'focusin',
      this.#fetchProductPageHandler
    );

    if (isDesktopBreakpoint()) {
      this.addEventListener(
        'pointerenter',
        this.#fetchProductPageHandler
      );

      this.addEventListener(
        'focusin',
        this.#fetchProductPageHandler
      );
    }
  };

  /**
   * Handles the variant selected event.
   *
   * @param {VariantSelectedEvent} event
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(
        event.detail.resource.id
      );
    }
  };

  /**
   * Handles the variant update event.
   *
   * IMPORTANT:
   * Do not rebuild the swatch picker here.
   *
   * @param {VariantUpdateEvent} event
   */
  #handleVariantUpdate = (event) => {
    event.stopPropagation();

    this.updatePrice(event);

    this.#isUnavailableVariantSelected(event);

    this.#updateProductUrl(event);

    this.refs.quickAdd?.fetchProductPage(
      this.productPageUrl
    );

    /*
     * Do not call:
     *
     * this.variantPicker?.updateVariantPicker(...)
     *
     * Swatch DOM must remain untouched.
     */

    this.#updateVariantImages();

    this.#previousSlideIndex = null;

    this.removeAttribute(
      'data-no-swatch-selected'
    );

    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate.
   */
  #updateOverflowList() {
    const overflowList =
      this.querySelector(
        'swatches-variant-picker-component overflow-list'
      );

    const isActiveOverflowList =
      overflowList?.querySelector('[slot="overflow"]')
        ? true
        : false;

    if (!overflowList || !isActiveOverflowList) {
      return;
    }

    requestAnimationFrame(() => {
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   *
   * @param {VariantUpdateEvent} event
   */
  updatePrice(event) {
    const priceContainer =
      this.querySelectorAll(
        `product-price [ref='priceContainer']`
      )[1];

    const newPriceElement =
      event.detail.data.html.querySelector(
        `product-price [ref='priceContainer']`
      );

    if (newPriceElement && priceContainer) {
      morph(
        priceContainer,
        newPriceElement
      );
    }
  }

  /**
   * Updates the product URL.
   *
   * @param {VariantUpdateEvent} event
   */
  #updateProductUrl(event) {
    const anchorElement =
      event.detail.data.html?.querySelector(
        'product-card a'
      );

    const featuredMediaUrl =
      event.detail.data.html
        ?.querySelector('product-card-link')
        ?.getAttribute(
          'data-featured-media-url'
        );

    if (
      featuredMediaUrl &&
      this.closest('product-card-link')
    ) {
      this.closest('product-card-link')
        ?.setAttribute(
          'data-featured-media-url',
          featuredMediaUrl
        );
    }

    if (
      anchorElement instanceof HTMLAnchorElement
    ) {
      if (
        anchorElement
          .getAttribute('href')
          ?.trim() === ''
      ) {
        return;
      }

      const productUrl =
        anchorElement.href;

      const {
        productCardLink,
        productTitleLink,
        cardGalleryLink,
      } = this.refs;

      productCardLink.href =
        productUrl;

      if (
        cardGalleryLink instanceof
        HTMLAnchorElement
      ) {
        cardGalleryLink.href =
          productUrl;
      }

      if (
        productTitleLink instanceof
        HTMLAnchorElement
      ) {
        productTitleLink.href =
          productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   *
   * @param {VariantUpdateEvent} event
   */
  #isUnavailableVariantSelected(event) {
    const allVariants =
      /** @type {NodeListOf<HTMLInputElement>} */ (
        event.detail.data.html.querySelectorAll(
          'input:checked'
        )
      );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(
        variant.dataset.optionAvailable === 'true'
      );
    }
  }

  /**
   * Toggles add to cart button.
   *
   * @param {boolean} enable
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton =
      this.querySelector(
        '.add-to-cart__button button'
      );

    if (
      addToCartButton instanceof
      HTMLButtonElement
    ) {
      addToCartButton.disabled =
        !enable;
    }
  }

  /**
   * Updates variant images.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;

    if (
      !this.variantPicker?.selectedOption
    ) {
      return;
    }

    const selectedImageId =
      this.variantPicker.selectedOption
        .dataset.optionMediaId;

    if (
      slideshow &&
      selectedImageId
    ) {
      const { slides = [] } =
        slideshow.refs;

      for (const slide of slides) {
        if (
          slide.getAttribute(
            'variant-image'
          ) == null
        ) {
          continue;
        }

        slide.hidden =
          slide.getAttribute(
            'slide-id'
          ) !== selectedImageId;
      }

      slideshow.select(
        { id: selectedImageId },
        undefined,
        { animate: false }
      );
    }
  }

  /**
   * Gets all variant inputs.
   */
  get allVariants() {
    return this.querySelectorAll(
      'input[data-variant-id]'
    );
  }

  /**
   * Gets variant picker.
   */
  get variantPicker() {
    return this.querySelector(
      'swatches-variant-picker-component'
    );
  }

  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles slideshow select.
   *
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex =
        event.detail.index;
    }
  };

  /**
   * Preview variant.
   *
   * @param {string} id
   */
  previewVariant(id) {
    const { slideshow } =
      this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    slideshow.select(
      { id },
      undefined,
      { animate: false }
    );
  }

  /**
   * Preview next image.
   *
   * @param {PointerEvent} event
   */
  previewImage(event) {
    if (
      event.pointerType !== 'mouse'
    ) {
      return;
    }

    const { slideshow } =
      this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (
      this.#previousSlideIndex != null &&
      this.#previousSlideIndex > 0
    ) {
      slideshow.select(
        this.#previousSlideIndex,
        undefined,
        { animate: false }
      );
    } else {
      slideshow.next(
        undefined,
        { animate: false }
      );

      setTimeout(() =>
        this.#preloadNextPreviewImage()
      );
    }
  }

  /**
   * Resets image.
   *
   * @param {PointerEvent} event
   */
  resetImage(event) {
    if (
      event.pointerType !== 'mouse'
    ) {
      return;
    }

    const { slideshow } =
      this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;

      slideshow.previous(
        undefined,
        { animate: false }
      );
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets variant image.
   */
  #resetVariant = () => {
    const { slideshow } =
      this.refs;

    if (!slideshow) return;

    if (
      this.variantPicker?.selectedOption
    ) {
      const id =
        this.variantPicker.selectedOption
          .dataset.optionMediaId;

      if (id) {
        slideshow.select(
          { id },
          undefined,
          { animate: false }
        );

        return;
      }
    }

    const initialSlide =
      slideshow.initialSlide;

    const slideId =
      initialSlide?.getAttribute(
        'slide-id'
      );

    if (
      initialSlide &&
      slideshow.slides?.includes(
        initialSlide
      ) &&
      slideId
    ) {
      slideshow.select(
        { id: slideId },
        undefined,
        { animate: false }
      );

      return;
    }

    slideshow.previous(
      undefined,
      { animate: false }
    );
  };

  /**
   * Navigate to product.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (
      !(event.target instanceof Element)
    ) {
      return;
    }

    if (
      this.hasAttribute(
        'data-no-navigation'
      )
    ) {
      return;
    }

    if (
      event.target.closest(
        'swatches-variant-picker-component'
      )
    ) {
      return;
    }

    const interactiveElement =
      event.target.closest(
        'button, input, label, select, [tabindex="1"]'
      );

    if (interactiveElement) {
      return;
    }

    const link =
      this.refs.productCardLink;

    if (!link.href) return;

    const linkURL =
      new URL(link.href);

    const productCardAnchor =
      link.getAttribute('id');

    if (!productCardAnchor) {
      return;
    }

    const infiniteResultsList =
      this.closest(
        'results-list[infinite-scroll="true"]'
      );

    if (
      !window.Shopify.designMode &&
      infiniteResultsList
    ) {
      const url =
        new URL(window.location.href);

      const parent =
        this.closest('li');

      url.hash =
        productCardAnchor;

      if (
        parent &&
        parent.dataset.page
      ) {
        url.searchParams.set(
          'page',
          parent.dataset.page
        );
      }

      yieldToMainThread().then(() => {
        history.replaceState(
          {},
          '',
          url.toString()
        );
      });
    }

    const targetLink =
      event.target.closest('a');

    if (!targetLink) {
      this.#navigateToURL(
        event,
        linkURL
      );
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant =
    debounce(
      this.#resetVariant,
      100
    );
}

if (
  !customElements.get(
    'product-card'
  )
) {
  customElements.define(
    'product-card',
    ProductCard
  );
}


/**
 * Swatches Variant Picker Component
 *
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {
 *   overflowList: HTMLElement
 * }} SwatchesRefs
 *
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    this.parentProductCard =
      this.closest('product-card');

    this.addEventListener(
      'click',
      this.#stopSwatchCardNavigation
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener(
      'click',
      this.#stopSwatchCardNavigation
    );
  }

  #stopSwatchCardNavigation = (event) => {
    event.stopPropagation();
  };

  /**
   * LINO PERROS TEMPORARY SWATCH BEHAVIOUR:
   *
   * Clicking a product-card swatch opens the
   * selected product / variant PDP directly.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) {
      return;
    }

    const clickedSwatch = event.target;

    const isSwatchInput =
      clickedSwatch instanceof HTMLInputElement &&
      clickedSwatch.name?.includes('-swatch');

    if (!isSwatchInput) {
      super.variantChanged(event);
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    /*
     * Variant ID belonging to clicked swatch.
     */
    const variantId =
      clickedSwatch.dataset.firstAvailableOrFirstVariantId ||
      clickedSwatch.dataset.variantId;

    /*
     * If this swatch belongs to a connected product,
     * use that product's URL.
     *
     * Otherwise use the current product URL.
     */
    const connectedProductUrl =
      clickedSwatch.dataset.connectedProductUrl;

    const baseProductUrl =
      connectedProductUrl ||
      this.dataset.productUrl;

    if (!baseProductUrl) {
      return;
    }

    /*
     * Build clean PDP URL.
     */
    const url = new URL(
      baseProductUrl.split('?')[0],
      window.location.origin
    );

    /*
     * Open the exact selected variant where available.
     */
    if (variantId) {
      url.searchParams.set(
        'variant',
        variantId
      );
    }

    /*
     * Go directly to PDP.
     */
    window.location.href =
      url.toString();
  }

  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } =
      this.refs;

    if (
      overflowList instanceof OverflowList
    ) {
      overflowList.showAll();
    }
  }
}

if (
  !customElements.get(
    'swatches-variant-picker-component'
  )
) {
  customElements.define(
    'swatches-variant-picker-component',
    SwatchesVariantPickerComponent
  );
}

/*
 * ============================================
 * LINO PERROS TEMP THUMBNAIL -> PDP
 * Shadow DOM safe
 * ============================================
 */

document.addEventListener(
  'pointerdown',
  (event) => {
    const path = event.composedPath();

    const thumbnail = path.find(
      (element) =>
        element instanceof HTMLElement &&
        element.classList.contains('lp-thumbnail-pdp-link')
    );

    if (!(thumbnail instanceof HTMLElement)) {
      return;
    }

    const url = thumbnail.getAttribute('data-pdp-url');

    if (!url) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    window.location.assign(url);
  },
  true
);